import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Mail, HardDrive, Calendar, CheckCircle, XCircle, Loader2 } from 'lucide-react';


export default function GoogleConnectPage() {
  const [status, setStatus] = useState(null); // { connected, email }
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    loadStatus();
    if (params.get('success')) { alert('Google 계정 연결 완료!'); navigate('/google/connect', { replace: true }); }
    if (params.get('error')) { alert('연결 실패: ' + params.get('error')); navigate('/google/connect', { replace: true }); }
  }, []);

  async function loadStatus() {
    try {
      const r = await api.get('/google/status');
      setStatus(r.data);
    } catch { setStatus({ connected: false, email: null }); }
    finally { setLoading(false); }
  }

  async function handleConnect() {
    setConnecting(true);
    try {
      const r = await api.get('/google/auth-url', { params: { origin: window.location.origin } });
      window.location.href = r.data.url;
    } catch (e) {
      alert('연결 URL 생성 실패: ' + e.message);
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm('Google 계정 연결을 해제하시겠습니까?')) return;
    await api.delete('/google/disconnect');
    await loadStatus();
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin text-gray-400" size={28} />
    </div>
  );

  return (
    <div className="max-w-lg mx-auto py-12 px-4">
      <h1 className="text-2xl font-bold mb-2 text-gray-100">Google Workspace 연동</h1>
      <p className="text-gray-400 text-sm mb-8">Google 계정을 연결하면 메일·드라이브·캘린더를 바로 사용할 수 있습니다.</p>

      <div className={`rounded-xl border p-5 mb-6 ${status?.connected ? 'border-green-500/40 bg-green-500/5' : 'border-gray-700 bg-gray-800/40'}`}>
        {status?.connected ? (
          <div className="flex items-center gap-3">
            <CheckCircle className="text-green-400 flex-shrink-0" size={22} />
            <div>
              <p className="font-semibold text-green-300">연결됨</p>
              <p className="text-sm text-gray-400">{status.email}</p>
            </div>
            <button onClick={handleDisconnect} className="ml-auto text-sm text-red-400 hover:text-red-300 transition-colors">
              연결 해제
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <XCircle className="text-gray-500 flex-shrink-0" size={22} />
            <div>
              <p className="font-semibold text-gray-300">연결 안 됨</p>
              <p className="text-sm text-gray-500">Google 계정을 연결해주세요.</p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-8">
        {[
          { icon: Mail, label: '메일', desc: 'Gmail 수신·발신' },
          { icon: HardDrive, label: '드라이브', desc: '파일 관리·공유' },
          { icon: Calendar, label: '캘린더', desc: '일정 연동' },
        ].map(({ icon: Icon, label, desc }) => (
          <div key={label} className="rounded-lg border border-gray-700 bg-gray-800/30 p-3 text-center">
            <Icon size={20} className="mx-auto mb-1.5 text-gray-400" />
            <p className="text-xs font-semibold text-gray-300">{label}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">{desc}</p>
          </div>
        ))}
      </div>

      {!status?.connected && (
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-white text-gray-900 font-semibold text-sm hover:bg-gray-100 transition-colors disabled:opacity-60"
        >
          {connecting ? <Loader2 size={16} className="animate-spin" /> : (
            <svg viewBox="0 0 24 24" className="w-4 h-4">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          Google 계정으로 연결
        </button>
      )}

      {status?.connected && (
        <div className="flex flex-col gap-3">
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-600 bg-gray-800 hover:bg-gray-700 text-sm font-semibold text-gray-300 transition-colors disabled:opacity-60"
          >
            {connecting ? <Loader2 size={14} className="animate-spin" /> : (
              <svg viewBox="0 0 24 24" className="w-4 h-4">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            Google 계정 재연결 (권한 업데이트)
          </button>
          <div className="flex gap-3">
            <button onClick={() => navigate('/gmail')} className="flex-1 py-2.5 rounded-xl bg-gray-700 hover:bg-gray-600 text-sm font-semibold text-gray-200 transition-colors flex items-center justify-center gap-2">
              <Mail size={14} /> 메일 열기
            </button>
            <button onClick={() => navigate('/drive')} className="flex-1 py-2.5 rounded-xl bg-gray-700 hover:bg-gray-600 text-sm font-semibold text-gray-200 transition-colors flex items-center justify-center gap-2">
              <HardDrive size={14} /> 드라이브 열기
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
